/**
 * usePhoneValidation Hook
 * Implements "Reward Early, Punish Late" validation pattern
 *
 * Key Principles:
 * - Show success (valid format) immediately
 * - Delay error display until user has:
 *   1. Blurred the field, OR
 *   2. Typed enough characters (expectedDigits - 2)
 * - Reset timing on country change to avoid premature errors
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import type { CountryCode } from 'libphonenumber-js';

import type {
  ValidationTiming,
  ValidationState,
  PhoneValidationError,
  UsePhoneValidationReturn,
} from '../types';
import {
  isPhoneValid,
  getDigitInfo,
  validatePhone,
  getErrorMessage,
} from '../utils/phoneParser';
import { getCountryName, getExpectedDigits } from '../utils/countryMetadata';

interface UsePhoneValidationOptions {
  value: string;
  country: CountryCode;
  required?: boolean;
  externalError?: string;
  onValidationChange?: (isValid: boolean) => void;
  onValidationError?: (error: PhoneValidationError | null) => void;
}

export function usePhoneValidation({
  value,
  country,
  required = false,
  externalError,
  onValidationChange,
  onValidationError,
}: UsePhoneValidationOptions): UsePhoneValidationReturn {
  // Validation timing state machine
  const [timing, setTiming] = useState<ValidationTiming>('idle');

  // Track previous country to detect changes
  const prevCountryRef = useRef<CountryCode>(country);

  // Compute validation state
  const isValid = useMemo(() => isPhoneValid(value), [value]);
  const digitInfo = useMemo(() => getDigitInfo(value, country), [value, country]);
  const error = useMemo(
    () => validatePhone(value, country, required),
    [value, country, required]
  );

  // Get expected digits for threshold calculation
  const { min: expectedMin } = useMemo(() => getExpectedDigits(country), [country]);

  /**
   * Determine if we should show error based on timing rules
   *
   * "Reward Early, Punish Late":
   * - Always show success immediately
   * - Show error only when:
   *   1. User has blurred the field, OR
   *   2. User has typed at least (expectedDigits - 2) digits
   *   3. There's an external error
   */
  const shouldShowError = useMemo(() => {
    // External error always shows
    if (externalError) return true;

    // No error to show
    if (!error) return false;

    // Always show after blur
    if (timing === 'blurred') return true;

    // During typing, only show if user has typed enough digits
    if (timing === 'typing') {
      const threshold = Math.max(expectedMin - 2, 3); // At least 3 digits
      return digitInfo.current >= threshold;
    }

    // Idle or pristine: don't show errors yet
    return false;
  }, [timing, error, externalError, digitInfo, expectedMin]);

  /**
   * Compute display state for styling
   */
  const state = useMemo((): ValidationState => {
    if (externalError || (shouldShowError && error)) return 'invalid';
    if (value && isValid) return 'valid';
    if (value && !digitInfo.isValid) return 'warning';
    return 'idle';
  }, [value, isValid, error, externalError, shouldShowError, digitInfo]);

  /**
   * Get error message for display
   */
  const errorMessage = useMemo(() => {
    if (externalError) return externalError;
    if (!shouldShowError || !error) return null;
    return getErrorMessage(error, getCountryName(country));
  }, [externalError, shouldShowError, error, country]);

  /**
   * Event handlers
   */
  const handleFocus = useCallback(() => {
    if (timing === 'idle') {
      setTiming('pristine');
    }
  }, [timing]);

  const handleBlur = useCallback(() => {
    setTiming('blurred');
  }, []);

  const handleChange = useCallback((_value: string) => {
    // Transition to typing state on first input
    // Note: _value is used by caller; we only care about timing transition
    if (timing === 'idle' || timing === 'pristine') {
      setTiming('typing');
    }
  }, [timing]);

  const resetValidation = useCallback(() => {
    // Reset to pristine so errors don't show immediately
    setTiming('pristine');
  }, []);

  /**
   * Reset timing when country changes
   * This prevents immediate error display after country switch
   */
  useEffect(() => {
    if (prevCountryRef.current !== country) {
      prevCountryRef.current = country;
      // Reset to pristine to allow user to input without seeing errors
      if (timing === 'blurred' || timing === 'typing') {
        setTiming('pristine');
      }
    }
  }, [country, timing]);

  /**
   * Notify parent of validation changes
   */
  useEffect(() => {
    onValidationChange?.(isValid);
  }, [isValid, onValidationChange]);

  useEffect(() => {
    onValidationError?.(error);
  }, [error, onValidationError]);

  return {
    isValid,
    error,
    errorMessage,
    timing,
    state,
    shouldShowError,
    digitInfo,
    handleFocus,
    handleBlur,
    handleChange,
    resetValidation,
  };
}
